// src/utils/practitionerDirectoryMapper.js
//
// Single source of truth for turning a real `practitioners/{uid}` Firestore
// document into the shape the directory card + filter/sort logic expects.
//
// This mirrors (and is intended to converge with) the mapping in the
// seekerDiscovery Cloud Function — `functions/services/seeker-discovery-service.js`.
// PR3 of the directory-wiring effort will fix that CF's two known defects
// (it reads `website_published.services` instead of the canonical
// `website_published.content.services[]`, and reads a non-existent
// `sessionFormats` field) and share this logic so the two surfaces never
// diverge. Until then, keep the field paths here authoritative.

// serviceType ('virtual' | 'in-person' | 'hybrid') → display session formats.
// 'hybrid' surfaces both chips, matching the directory's exact label values.
const SERVICE_TYPE_TO_FORMATS = {
  virtual: ['Virtual'],
  'in-person': ['In-Person'],
  hybrid: ['Virtual', 'In-Person'],
};

/**
 * Derive the directory's `sessionFormats` (`['Virtual', 'In-Person']`) from a
 * service list's `serviceType` values. Returns a de-duplicated array.
 */
export function deriveSessionFormats(services) {
  const formats = new Set();
  (services || []).forEach((s) => {
    (SERVICE_TYPE_TO_FORMATS[s?.serviceType] || []).forEach((f) => formats.add(f));
  });
  return Array.from(formats);
}

/**
 * Compute `{ min, max }` price (USD dollars) over a service list.
 * Returns null when there are no priceable services so callers can hide the
 * price element gracefully (free services with `price: 0` are included).
 */
export function computePriceRange(services) {
  const prices = (services || [])
    .map((s) => Number(s?.price))
    .filter((p) => Number.isFinite(p));
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/**
 * Map a practitioner Firestore doc to the directory-card shape.
 *
 * @param {string} id    — Firestore doc id (practitioner uid)
 * @param {object} data  — `practitioners/{uid}` document data
 * @returns {object} directory-card-shaped practitioner
 */
export function mapPractitionerDocToDirectoryCard(id, data) {
  const d = data || {};
  const pub = d.website_published || {};
  const profile = d.profile || {};
  const pubProfile = pub.profile || {};
  const hero = pub.hero || {};
  const reviewStats = d.reviewStats || {};

  // Canonical services path: website_published.content.services[]
  // (NOT website_published.services — that wrong path is the CF bug PR3 fixes.)
  const allServices = Array.isArray(pub.content && pub.content.services)
    ? pub.content.services
    : [];
  const services = allServices.filter((s) => s && s.name);

  const displayName =
    profile.displayName ||
    pubProfile.displayName ||
    hero.headline ||
    d.displayName ||
    null;

  // Real modalities shape — { selected[], custom[], parentCategories[] } —
  // already matches what getModalityDisplayNames() + the filters expect.
  const modalities = profile.modalities || pubProfile.modalities || d.modalities || {};

  // reviewStats.averageRating is null/absent for practitioners with no reviews.
  // Keep it null (do NOT coerce to 0) so the card can render a "New" treatment
  // rather than a misleading 0.0 rating.
  const averageRating =
    typeof reviewStats.averageRating === 'number' ? reviewStats.averageRating : null;
  const reviewCount = reviewStats.totalReviews || 0;

  return {
    id,
    slug: d.slug || null,
    displayName,
    tagline: hero.subheadline || hero.tagline || profile.tagline || pubProfile.tagline || '',
    bio: profile.bio || pubProfile.bio || hero.bio || '',
    photo: profile.photo || pubProfile.photo || null,
    verification: d.verification || {},
    modalities,
    // Search consumes `services[].name` only; keep it minimal.
    services: services.map((s) => ({ name: s.name })),
    // Decision 2: display location if present; the card falls back to "Virtual".
    location: profile.location || d.location || null,
    sessionFormats: deriveSessionFormats(services),
    priceRange: computePriceRange(services),
    rating: averageRating,
    reviewCount,
    hasReviews: reviewCount > 0,
  };
}
